import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Globe, Eye, Users, Monitor, Smartphone, Tablet, BarChart3, ArrowUpRight, MapPin, Link, Loader2, Calendar } from 'lucide-react';

const API = '';

export default function SiteAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/analytics/summary?days=${days}`, { withCredentials: true });
        setData(res.data);
      } catch (err) {
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="analytics-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading analytics...</span>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 text-center text-slate-500" data-testid="analytics-empty">
        No analytics data available yet.
      </div>
    );
  }

  const maxDailyCount = Math.max(...(data.daily || []).map(d => d.count), 1);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="site-analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Site Analytics</h1>
          <p className="text-xs text-slate-500 mt-0.5">
            {data.source === 'posthog' ? 'PostHog' : 'Custom tracker'} &middot; Last {data.period_days} days
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="analytics-period-selector">
          {[7, 14, 30, 90].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)} className="h-7 text-xs px-3">
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-total-views">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-50"><Eye className="w-5 h-5 text-sky-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{(data.total_views || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Page Views</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-unique-visitors">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50"><Users className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{(data.unique_visitors || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Unique Visitors</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-countries">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-50"><Globe className="w-5 h-5 text-violet-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{(data.countries || []).length}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Countries</p>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-pages">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50"><BarChart3 className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{(data.pages || []).length}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Pages</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Trend */}
      {data.daily && data.daily.length > 0 && (
        <Card data-testid="daily-trend">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" /> Daily Traffic
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="flex items-end gap-[2px] h-32">
              {data.daily.map((d, i) => (
                <div key={i} className="flex-1 group relative cursor-pointer" title={`${d.date}: ${d.count} views`}>
                  <div className="bg-sky-500 hover:bg-sky-600 rounded-t-sm transition-colors w-full"
                    style={{ height: `${Math.max(2, (d.count / maxDailyCount) * 100)}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-[9px] text-slate-400">{data.daily[0]?.date}</span>
              <span className="text-[9px] text-slate-400">{data.daily[data.daily.length - 1]?.date}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Three columns */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Countries */}
        <Card data-testid="countries-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Globe className="w-4 h-4 text-slate-400" /> Top Countries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.countries || []).slice(0, 12).map((c, i) => {
                const maxCount = (data.countries?.[0]?.count) || 1;
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`country-row-${i}`}>
                    <span className="text-xs text-slate-600 w-32 truncate">{c.country || 'Unknown'}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className="h-full bg-sky-500 rounded-full" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-12 text-right">{c.count}</span>
                  </div>
                );
              })}
              {(!data.countries || data.countries.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-4">No country data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card data-testid="pages-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-slate-400" /> Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.pages || []).slice(0, 12).map((p, i) => {
                const maxCount = (data.pages?.[0]?.count) || 1;
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`page-row-${i}`}>
                    <span className="text-xs text-slate-600 w-32 truncate">{p.page}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(p.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-12 text-right">{p.count}</span>
                  </div>
                );
              })}
              {(!data.pages || data.pages.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-4">No page data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Cities */}
        <Card data-testid="cities-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4 text-slate-400" /> Top Cities
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.cities || []).slice(0, 12).map((c, i) => {
                const maxCount = (data.cities?.[0]?.count) || 1;
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`city-row-${i}`}>
                    <span className="text-xs text-slate-600 w-32 truncate">{c.city}, {c.country}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${(c.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-12 text-right">{c.count}</span>
                  </div>
                );
              })}
              {(!data.cities || data.cities.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-4">No city data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Devices */}
        <Card data-testid="devices-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Monitor className="w-4 h-4 text-slate-400" /> Devices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              {(data.devices || []).map((d, i) => {
                const total = data.total_views || 1;
                const pct = ((d.count / total) * 100).toFixed(1);
                const icon = d.device === 'Mobile' ? <Smartphone className="w-5 h-5" /> :
                  d.device === 'Tablet' ? <Tablet className="w-5 h-5" /> : <Monitor className="w-5 h-5" />;
                return (
                  <div key={i} className="text-center" data-testid={`device-${d.device}`}>
                    <div className="text-slate-400 flex justify-center">{icon}</div>
                    <p className="text-lg font-bold text-slate-900 mt-1">{pct}%</p>
                    <p className="text-[10px] text-slate-500 capitalize">{d.device}</p>
                    <p className="text-[10px] text-slate-400">{d.count}</p>
                  </div>
                );
              })}
              {(!data.devices || data.devices.length === 0) && (
                <p className="text-xs text-slate-400 py-4">No device data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Top Referrers */}
        <Card data-testid="referrers-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Link className="w-4 h-4 text-slate-400" /> Top Referrers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.referrers || []).slice(0, 8).map((r, i) => {
                const maxCount = (data.referrers?.[0]?.count) || 1;
                let domain = r.referrer;
                try { domain = new URL(r.referrer).hostname; } catch {}
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`referrer-row-${i}`}>
                    <span className="text-xs text-slate-600 w-40 truncate">{domain}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(r.count / maxCount) * 100}%` }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-12 text-right">{r.count}</span>
                  </div>
                );
              })}
              {(!data.referrers || data.referrers.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-4">No referrer data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {data.source && (
        <p className="text-[10px] text-slate-400 text-center">
          Data source: {data.source === 'posthog' ? 'PostHog Analytics' : 'Custom ForesightRx Tracker'}
        </p>
      )}
    </div>
  );
}
